/**
 * SocialProfileTemplate.tsx — Instagram ID WebScan and similar single-profile
 * scans. Covers: combined_id when the entity carries `type: "ProfileData"`.
 *
 * Small payload, so the value added here is the "Not returned" panel — telling
 * the user that bio and links are simply not part of this workflow stops the
 * "is it broken or is there nothing?" question.
 *
 * Highlights/Stories carousels: field names confirmed against
 * component/searcher/RenderData.tsx's RenderProfileData, the old UI's
 * proven-working implementation for this exact payload shape —
 * `{ thumbnail_url, highlight_name, highlight_content }` for highlights,
 * `{ url | thumbnailUrl | thumbnail_url, taken_at }` for stories (story
 * media can be a photo or a video; falls back to <video> on img load
 * failure). HighlightsRow/StoriesRow below mirror that implementation
 * closely — visible-tile count measured directly off the scroll container's
 * ref during render, index-based smooth scrollTo — rather than reinventing
 * the measurement approach, since that's the version confirmed to actually
 * render real data. Arrow buttons use our own icon-button style (see
 * usePhotoLightbox's prev/next in ui/primitives.tsx), not the old UI's.
 *
 * Posts: insta_story's own captured payload only ever returns `posts_count`
 * (a bare number) — verified against the real INSTAGRAM_WEBSCAN fixture in
 * container/preview/results.ts, which has a single-row `data` array with no
 * post objects anywhere. But individual posts DO exist as real data
 * elsewhere in this app: the dedicated "Posts observer" workflow (`type:
 * "post"`, see PostsTemplate.tsx) returns rows tagged `type: "PostItem"`
 * with real fields (images_url, text, like_count, ...), and
 * NameCommon.tsx's combined_name flow joins those PostItem rows to a
 * profile by matching `from` + `profile_id` as SIBLING rows in the same
 * entity.data array. `extract()` below does the identical join for
 * combined_id/insta_story — if the provider ever ships PostItem rows
 * alongside the ProfileData row (same shape, just a different source), the
 * Posts panel picks them up and renders real posts via ui/postCard.tsx
 * instead of the honest count-only fallback.
 */
import * as React from 'react'
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { networkMeta, radii } from '../tokens'
import {
  Cols, Duo, FieldTable, NoData, Photo, RawCard, ResCard, Sech, Stack, Stats, toImageSrc, usePhotoLightbox, ValueList, useTokens,
} from '../ui/primitives'
import { extractPostItems, PostList } from '../ui/postCard'
import type { TemplateProps } from '../registry'

type Profile = {
  network: string
  id: string
  name?: string
  image?: string
  bio?: string
  isVerified?: boolean
  posts?: number
  followers?: number
  following?: number
  // TikTok's ProfileItem row (irbis-social-tiktok) carries these directly on
  // the row, same as `bio`/`isVerified` above — insta_story never returns
  // any of them, so all stay optional and simply don't render when absent.
  likes?: number
  friends?: number
  userId?: string
  profileUrl?: string
  bioLink?: string
  profileCreatedAt?: string | number
  isPrivate? : boolean
  language?: string
  nicknameEditedAt?: string | number
  highlightsRaw: any[]
  storiesRaw: any[]
  postsRaw: any[]
  postItemRows: any[]
}

/** A row describing a social profile — same loose duck-type registry.ts's
 * own `rowIsProfile` uses, kept local rather than imported since that file
 * has no exports meant for template consumption. */
const looksLikeProfileRow = (r: any): boolean =>
  !!r && typeof r === 'object' &&
  (r.type === 'ProfileItem' || r.type === 'ProfileData' || !!r.social_network || !!r.profile_id || !!r.profileData)

/**
 * `irbis-social-tiktok` (and presumably any sibling `irbis-social-*`
 * source) wraps its rows one level deeper than insta_story/etc: `e.data` is
 * a single-item array holding `{ ok, source, totalItems, data: [ ...real
 * ProfileItem/PostItem rows... ] }` — a summary envelope, not a row. This
 * unwraps that one specific shape so the rest of `extract()` below (written
 * for insta_story's flat `e.data` = row array) sees the real rows.
 */
const rowsOf = (e: any): any[] => {
  const data = e?.data
  if (!Array.isArray(data)) return []
  const only = data.length === 1 ? data[0] : null
  if (only && typeof only === 'object' && Array.isArray(only.data) && !looksLikeProfileRow(only) && !('password' in only)) {
    return only.data
  }
  return data
}

function extract(apiData: any): Profile | null {
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  for (const e of entities) {
    const rows = rowsOf(e)
    for (const r of rows) {
      if (!r || typeof r !== 'object') continue
      const d = r.profileData || {}
      if (r.social_network || d.posts_count !== undefined) {
        const highlightsRaw = Array.isArray(d.highlights) ? d.highlights : []
        const storiesRaw = Array.isArray(d.stories) ? d.stories : []
        // insta_story itself never returns these — kept for any other
        // combined_id source that lands on this template and does.
        const postsRaw = Array.isArray(d.posts)
          ? d.posts
          : Array.isArray(d.recent_posts)
          ? d.recent_posts
          : Array.isArray(d.media)
          ? d.media
          : []
        // Real individual posts (see file-header note): sibling `PostItem`
        // rows in this same entity, tied to this profile by from+profile_id.
        const rowFrom = String(r.from ?? e.from ?? '').toLowerCase()
        const rowProfileId = String(r.profile_id ?? '')
        const postItemRows = rows.filter(
          (x: any) =>
            x && typeof x === 'object' && x.type === 'PostItem' &&
            String(x.from ?? e.from ?? '').toLowerCase() === rowFrom &&
            String(x.profile_id ?? '') === rowProfileId,
        )
        return {
          network: String(r.social_network || e.from || 'unknown').toUpperCase(),
          id: String(r.profile_id ?? apiData?.criteria ?? ''),
          name: r.profileName || undefined,
          image: r.profileImageUrl || undefined,
          // TikTok's ProfileItem row (irbis-social-tiktok) has shown up with
          // these fields both directly on the row AND nested under
          // `profileData` (real payload: `profileData.url`, not `r.url`) —
          // checking `r` first, `d` second covers either shape without
          // guessing which one a given response used.
          bio: r.biography || d.biography || undefined,
          isVerified: r.is_verified === true || d.is_verified === true,
          posts: d.posts_count,
          followers: d.followers_count,
          following: d.following_count,
          likes: typeof r.likes_count === 'number' ? r.likes_count : typeof d.likes_count === 'number' ? d.likes_count : undefined,
          friends: typeof r.friends_count === 'number' ? r.friends_count : typeof d.friends_count === 'number' ? d.friends_count : undefined,
          userId: r.user_id ? String(r.user_id) : d.user_id ? String(d.user_id) : undefined,
          profileUrl: r.url || d.url || undefined,
          bioLink: r.bio_link || d.bio_link || undefined,
          profileCreatedAt: r.profile_created_at ?? d.profile_created_at ?? undefined,
          isPrivate: r.is_private ?? d.is_private ?? undefined,
          language: r.language || d.language || undefined,
          nicknameEditedAt: r.nickname_edited_at ?? d.nickname_edited_at ?? undefined,
          highlightsRaw,
          storiesRaw,
          postsRaw,
          postItemRows,
        }
      }
    }
  }
  return null
}

/* ------------------------------------------------------------------ */
/* Highlights / Stories carousels                                      */
/* ------------------------------------------------------------------ */

type HighlightItem = { key: string; thumb: string; name: string; content?: string }
type StoryItem = { key: string; src: string; takenAt?: number }

function extractHighlights(raw: any[]): HighlightItem[] {
  return raw.map((h, i) => ({
    key: String(h?.id ?? h?.pk ?? i),
    thumb: toImageSrc(h?.thumbnail_url ?? h?.thumbnail ?? ''),
    name: h?.highlight_name || '—',
    content: h?.highlight_content || undefined,
  }))
}

function formatTakenAt(v: number): string {
  const ms = v > 1e12 ? v : v * 1000
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** `profile_created_at`/`nickname_edited_at` have shown up as either an
 * epoch (seconds or milliseconds, same ambiguity `formatTakenAt` above
 * handles for story timestamps) or a plain ISO/date string — this accepts
 * either. Kept separate from `formatTakenAt` because these two fields are
 * shown down to the second (unlike story timestamps, minute precision is
 * enough there). */
function formatDateValue(v: string | number): string {
  const d = typeof v === 'number' ? new Date(v > 1e12 ? v : v * 1000) : new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function extractStories(raw: any[]): StoryItem[] {
  return raw
    .map((s, i) => ({
      key: String(s?.id ?? s?.pk ?? i),
      src: toImageSrc(s?.url ?? s?.thumbnailUrl ?? s?.thumbnail_url ?? ''),
      takenAt: typeof s?.taken_at === 'number' ? s.taken_at : undefined,
    }))
    .filter((s) => !!s.src)
}

/** Round icon-button matching the lightbox's prev/next style — this is "our
 * style" for carousel arrows, kept consistent across the app. */
function CarouselArrow({ direction, onClick, disabled }: { direction: 'left' | 'right'; onClick: () => void; disabled?: boolean }) {
  const tk = useTokens()
  return (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      sx={{ background: tk.surface2, '&:hover': { background: tk.surface3 }, '&.Mui-disabled': { opacity: 0.35 } }}
    >
      {direction === 'left' ? <ChevronLeftIcon sx={{ fontSize: 18, color: tk.text }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: tk.text }} />}
    </IconButton>
  )
}

const scrollRowSx = {
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const

/** Visible-tile count measured directly off the container's current width —
 * same approach as the old UI's proven RenderProfileData, deliberately not a
 * ResizeObserver/effect: reading ref.current during render means the very
 * first paint (ref not yet attached) safely falls back to 1, and every
 * render after mount reads the real width. The tile count only drives the
 * counter text and arrow disabled state — every item is always in the DOM,
 * so a wrong count never hides content, only the "3 / 10" label. */
function useVisibleCount(containerRef: React.RefObject<HTMLDivElement>, cardWidth: number) {
  const el = containerRef.current
  return el ? Math.max(1, Math.floor(el.clientWidth / cardWidth)) : 1
}

const HIGHLIGHT_SIZE = 70 // 100 * 0.7 — 30% smaller than the original avatar size

function HighlightsRow({ items }: { items: HighlightItem[] }) {
  const tk = useTokens()
  const ref = React.useRef<HTMLDivElement>(null)
  const [index, setIndex] = React.useState(0)
  const cardWidth = HIGHLIGHT_SIZE + 16
  const visibleCount = useVisibleCount(ref, cardWidth)
  const maxIndex = Math.max(0, items.length - visibleCount)
  const { openAt, lightbox } = usePhotoLightbox(items.map((h) => h.thumb))

  const scroll = (dir: 'left' | 'right') => {
    const next = dir === 'left' ? Math.max(0, index - 1) : Math.min(maxIndex, index + 1)
    ref.current?.scrollTo({ left: next * cardWidth, behavior: 'smooth' })
    setIndex(next)
  }

  if (items.length === 0) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px' }}>
        <Sech>Highlights</Sech>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>
            {Math.min(index + visibleCount, items.length)} / {items.length}
          </Typography>
          <CarouselArrow direction="left" onClick={() => scroll('left')} disabled={index === 0} />
          <CarouselArrow direction="right" onClick={() => scroll('right')} disabled={index >= maxIndex} />
        </Box>
      </Box>
      <Box ref={ref} sx={scrollRowSx}>
        {items.map((h, i) => (
          <Tooltip key={h.key} title={h.content || ''} disableHoverListener={!h.content} arrow>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: HIGHLIGHT_SIZE, maxWidth: HIGHLIGHT_SIZE, flexShrink: 0 }}>
              <Box
                sx={{
                  width: HIGHLIGHT_SIZE, height: HIGHLIGHT_SIZE, borderRadius: '50%', overflow: 'hidden', flex: 'none',
                  background: tk.surface3, border: `1px solid ${tk.border}`,
                }}
              >
                {h.thumb && <Photo value={h.thumb} onClick={() => openAt(i)} sx={{ cursor: 'default' }} />}
              </Box>
              <Typography sx={{ fontSize: 11, color: tk.text2, textAlign: 'center', lineHeight: 1.25, wordBreak: 'break-word' }}>
                {h.name}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
      {lightbox}
    </Box>
  )
}

/**
 * Story video: custom always-visible controls instead of the native
 * `controls` bar (which only appears on hover and disappears again) — a
 * centred play/pause, a seek bar, mute, fullscreen and a "more" menu
 * (open original / download), plus a shared `activeVideoRef` (one per
 * StoriesRow) so starting a second clip pauses whichever one was already
 * playing — native <video> elements don't coordinate with each other on
 * their own.
 */
const miniVideoBtnSx = {
  width: 22, height: 22, background: 'rgba(0,0,0,.55)', '&:hover': { background: 'rgba(0,0,0,.72)' },
} as const

function StoryVideo({ src, activeVideoRef }: { src: string; activeVideoRef: React.MutableRefObject<HTMLVideoElement | null> }) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [muted, setMuted] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    const el = videoRef.current
    if (!el) return
    if (el.paused) {
      if (activeVideoRef.current && activeVideoRef.current !== el) {
        activeVideoRef.current.pause()
      }
      el.play()
      activeVideoRef.current = el
    } else {
      el.pause()
    }
  }

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    const el = videoRef.current
    if (!el) return
    el.muted = !el.muted
    setMuted(el.muted)
  }

  const goFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation()
    const el = videoRef.current as any
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen || el?.webkitEnterFullscreen
    req?.call(el)
  }

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const el = videoRef.current
    if (!el || !el.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    el.currentTime = ratio * el.duration
    setProgress(ratio)
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        component="video"
        ref={videoRef}
        src={src}
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => {
          const el = e.currentTarget
          setProgress(el.duration ? el.currentTime / el.duration : 0)
        }}
        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />

      <IconButton
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 44, height: 44, background: 'rgba(0,0,0,.55)', '&:hover': { background: 'rgba(0,0,0,.72)' },
        }}
      >
        {isPlaying ? <PauseIcon sx={{ fontSize: 24, color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: 24, color: '#fff' }} />}
      </IconButton>

      <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: '3px' }}>
        <IconButton onClick={goFullscreen} aria-label="Fullscreen" sx={miniVideoBtnSx}>
          <FullscreenIcon sx={{ fontSize: 15, color: '#fff' }} />
        </IconButton>
        <IconButton onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget) }} aria-label="More" sx={miniVideoBtnSx}>
          <MoreVertIcon sx={{ fontSize: 15, color: '#fff' }} />
        </IconButton>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={(e: any) => { e?.stopPropagation?.(); setMenuAnchor(null) }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { window.open(src, '_blank', 'noopener'); setMenuAnchor(null) }}>Open original</MenuItem>
        <MenuItem
          onClick={() => {
            const a = document.createElement('a')
            a.href = src
            a.download = ''
            a.click()
            setMenuAnchor(null)
          }}
        >
          Download
        </MenuItem>
      </Menu>

      <Box sx={{ position: 'absolute', left: 4, bottom: 4 }}>
        <IconButton onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} sx={miniVideoBtnSx}>
          {muted ? <VolumeOffIcon sx={{ fontSize: 15, color: '#fff' }} /> : <VolumeUpIcon sx={{ fontSize: 15, color: '#fff' }} />}
        </IconButton>
      </Box>

      <Box
        onClick={seek}
        sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 4, background: 'rgba(255,255,255,.3)', cursor: 'pointer' }}
      >
        <Box sx={{ height: '100%', width: `${progress * 100}%`, background: '#fff' }} />
      </Box>
    </Box>
  )
}

function StoryTile({
  src,
  onOpen,
  activeVideoRef,
}: {
  src: string
  onOpen: () => void
  activeVideoRef: React.MutableRefObject<HTMLVideoElement | null>
}) {
  const tk = useTokens()
  const [isVideo, setIsVideo] = React.useState(false)
  return (
    <Box sx={{ width: '100%', height: '100%', background: tk.surface3, borderRadius: `${radii.md}px`, overflow: 'hidden' }}>
      {!isVideo ? (
        <Photo value={src} onLoadError={() => setIsVideo(true)} onClick={onOpen} sx={{ objectFit: 'cover' }} />
      ) : (
        <StoryVideo src={src} activeVideoRef={activeVideoRef} />
      )}
    </Box>
  )
}

/** Baseline: exactly this many story tiles would fit edge-to-edge per
 * "page" of the row, tile width derived from the container's actual width
 * rather than fixed. `STORY_SIZE_SCALE` then grows that baseline tile by
 * 20% — so a bit under 5 tiles are visible at once (the tail one peeking in
 * as a hint there's more to scroll to), rather than changing the "fit N"
 * count itself. Measured via ResizeObserver (not a one-shot mount
 * measurement) so it also recovers if the container's real width only
 * settles after the dialog's enter transition finishes. */
const STORIES_PER_VIEW = 5
const STORY_GAP = 12
const STORY_SIZE_SCALE = 1.2 // +20% over the "exactly 5 fit" baseline
const STORY_ASPECT = 172 / 116 // height:width ratio of the original fixed-size tile

function StoriesRow({ items }: { items: StoryItem[] }) {
  const tk = useTokens()
  const ref = React.useRef<HTMLDivElement>(null)
  const [index, setIndex] = React.useState(0)
  const [tileWidth, setTileWidth] = React.useState(Math.round(116 * STORY_SIZE_SCALE))
  const cardWidth = tileWidth + STORY_GAP
  const visibleCount = useVisibleCount(ref, cardWidth)
  const maxIndex = Math.max(0, items.length - visibleCount)
  const { openAt, lightbox } = usePhotoLightbox(items.map((s) => s.src))
  const activeVideoRef = React.useRef<HTMLVideoElement | null>(null)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const w = el.clientWidth
      if (w > 0) {
        const baseline = (w - STORY_GAP * (STORIES_PER_VIEW - 1)) / STORIES_PER_VIEW
        setTileWidth(Math.max(72, Math.floor(baseline * STORY_SIZE_SCALE)))
      }
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const scroll = (dir: 'left' | 'right') => {
    const next = dir === 'left' ? Math.max(0, index - 1) : Math.min(maxIndex, index + 1)
    ref.current?.scrollTo({ left: next * cardWidth, behavior: 'smooth' })
    setIndex(next)
  }

  if (items.length === 0) return null

  const tileHeight = Math.round(tileWidth * STORY_ASPECT)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px' }}>
        <Sech>Stories</Sech>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>
            {Math.min(index + visibleCount, items.length)} / {items.length}
          </Typography>
          <CarouselArrow direction="left" onClick={() => scroll('left')} disabled={index === 0} />
          <CarouselArrow direction="right" onClick={() => scroll('right')} disabled={index >= maxIndex} />
        </Box>
      </Box>
      <Box ref={ref} sx={scrollRowSx}>
        {items.map((s, i) => (
          <Box key={s.key} sx={{ minWidth: tileWidth, maxWidth: tileWidth, flexShrink: 0 }}>
            <Box sx={{ height: tileHeight }}>
              <StoryTile src={s.src} onOpen={() => openAt(i)} activeVideoRef={activeVideoRef} />
            </Box>
            <Typography sx={{ fontSize: 9.5, color: tk.text3, mt: '4px', textAlign: 'center', minHeight: 13 }}>
              {s.takenAt !== undefined ? formatTakenAt(s.takenAt) : ' '}
            </Typography>
          </Box>
        ))}
      </Box>
      {lightbox}
    </Box>
  )
}

type MediaItem = { key: string; image: string; label?: string }

/**
 * insta_story's highlights/stories items haven't been observed non-empty in
 * any captured payload, so the exact shape (plain label strings vs objects
 * with a thumbnail) is unconfirmed. This normalizes either: an object's
 * image is pulled from the common thumbnail/image/media/cover/url field
 * names, an object's caption from title/name/caption/label; a bare string is
 * tried as an image URL first and falls back to showing it as the label
 * (matches sampleData/combined_id.sample.json's own mock).
 */
function normalizeMediaItems(raw: any[], fallbackLabel: string): MediaItem[] {
  return raw.map((item, idx) => {
    if (item && typeof item === 'object') {
      const image = toImageSrc(
        item.thumbnail_url ?? item.thumbnail ?? item.image_url ?? item.image ?? item.media_url ?? item.cover ?? item.preview ?? item.url ?? '',
      )
      const label = item.title || item.name || item.caption || item.label
      return { key: String(item.id ?? item.pk ?? idx), image, label: label || (image ? undefined : `${fallbackLabel} ${idx + 1}`) }
    }
    const str = typeof item === 'string' ? item : String(item ?? '')
    const image = toImageSrc(str)
    return { key: `${idx}-${str}`, image, label: image ? undefined : (str || `${fallbackLabel} ${idx + 1}`) }
  })
}

/** One row per post — thumbnail on the left, label (if any) on the right —
 * stacked in a single column rather than wrapped into a grid. Click-to-
 * enlarge stays wired across just the image tiles. */
function MediaPanel({ items }: { items: MediaItem[] }) {
  const tk = useTokens()
  const { openAt, lightbox } = usePhotoLightbox(items.map((it) => it.image))

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((it, i) => (
          <Box
            key={it.key}
            sx={{
              display: 'flex', alignItems: 'center', gap: '10px', pb: i < items.length - 1 ? '8px' : 0,
              borderBottom: i < items.length - 1 ? `1px solid ${tk.border}` : 'none',
            }}
          >
            <Box
              sx={{
                width: 56, height: 56, borderRadius: `${radii.md}px`, overflow: 'hidden', flex: 'none',
                background: tk.surface3, border: `1px solid ${tk.border}`, display: it.image ? undefined : 'grid', placeItems: 'center',
              }}
            >
              {it.image ? (
                <Photo value={it.image} onClick={() => openAt(i)} />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={tk.text3} strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <circle cx="9" cy="10" r="1.7" />
                  <path d="m21 15-5-5-9 9" />
                </svg>
              )}
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tk.text2, wordBreak: 'break-word' }}>
              {it.label || `Post ${i + 1}`}
            </Typography>
          </Box>
        ))}
      </Box>
      {lightbox}
    </>
  )
}

export function SocialProfileTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const p = React.useMemo(() => extract(apiData), [apiData])
  const { openAt, lightbox } = usePhotoLightbox(p?.image ? [p.image] : [])
  const realPosts = React.useMemo(() => extractPostItems(p?.postItemRows ?? []), [p])
  const postItems = React.useMemo(() => normalizeMediaItems(p?.postsRaw ?? [], 'Post'), [p])
  const highlightItems = React.useMemo(() => extractHighlights(p?.highlightsRaw ?? []), [p])
  const storyItems = React.useMemo(() => extractStories(p?.storiesRaw ?? []), [p])

  if (!p) return <NoData title="No profile returned" hint="The handle or ID did not resolve to a public profile." />

  const ratio = p.followers && p.following ? (p.followers / p.following).toFixed(2) : null
  const meta = networkMeta(p.network)
  const highlightsCount = p.highlightsRaw.length
  const storiesCount = p.storiesRaw.length

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Posts', value: p.posts ?? '—' },
            { label: 'Followers', value: p.followers ?? '—' },
            { label: 'Following', value: p.following ?? '—' },
            ...(ratio ? [{ label: 'Ratio', value: ratio, sub: Number(ratio) < 1 ? 'follows more than followed' : 'followed more than follows', tone: Number(ratio) < 1 ? ('warn' as const) : undefined }] : []),
            // TikTok-only counters (irbis-social-tiktok) — omitted rather
            // than shown as "—" for every other source that never returns
            // them, same reasoning as `ratio` above. A genuine 0 is treated
            // the same as absent, since there's nothing informative to show.
            ...(p.likes ? [{ label: 'Likes', value: p.likes }] : []),
            ...(p.friends ? [{ label: 'Friends', value: p.friends }] : []),
            // A 0-count "none published"/"none active" tile isn't
            // informative — same treatment as Likes/Friends above.
            ...(highlightsCount ? [{ label: 'Highlights', value: highlightsCount }] : []),
            ...(storiesCount ? [{ label: 'Stories', value: storiesCount }] : []),
          ]}
        />
      </ResCard>

      <Cols n={2}>
        <Stack>
          <ResCard title="Profile">
            <Box sx={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
              {/* Avatar/name/badges stacked and centered above the field
               * table, instead of sitting beside it as a single row — reads
               * better once the field list grows past 2-3 rows, since the
               * identity block no longer stretches to match its height. */}
              <Box sx={{ flex: '0 0 160px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ width: 96, height: 96, borderRadius: '50%', background: tk.surface3, flex: 'none', overflow: 'hidden', display: 'grid', placeItems: 'center', color: tk.text3, mb: '12px' }}>
                  {p.image ? (
                    <Photo value={p.image} onClick={() => openAt(0)} />
                  ) : (
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="9" r="3.4" /><path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" /></svg>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: tk.text }}>{p.name || p.id}</Typography>
                <Typography sx={{ fontSize: 10.5, color: tk.text3, mb: '10px' }}>
                  {meta.label}
                  {!p.name && ' · profileName empty at source'}
                </Typography>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', justifyContent: 'center' }}>
                  <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '9px', py: '3px', borderRadius: '7px', background: meta.color, color: '#fff', whiteSpace: 'nowrap' }}>
                    {meta.label}
                  </Box>
                  <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '9px', py: '3px', borderRadius: '7px', background: tk.surface3, color: tk.text2, whiteSpace: 'nowrap' }}>
                    public
                  </Box>
                  {p.isVerified && (
                    <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '9px', py: '3px', borderRadius: '7px', background: '#10B981', color: '#fff', whiteSpace: 'nowrap' }}>
                      verified
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
              <FieldTable
                rows={[
                  ['Network', meta.label],
                  ['Profile ID', p.id],
                  ['Photo', p.image ? 'available' : 'not returned'],
                  // TikTok-only fields (irbis-social-tiktok) — each stays
                  // absent from the table rather than showing empty when the
                  // source doesn't return it, same as `bio` below.
                  ...(p.userId ? [['User ID', p.userId] as [string, React.ReactNode]] : []),
                  ...(p.profileCreatedAt !== undefined ? [['Created', formatDateValue(p.profileCreatedAt)] as [string, React.ReactNode]] : []),
                  ...(p.language ? [['Language', p.language] as [string, React.ReactNode]] : []),
                  ...(p.nicknameEditedAt !== undefined ? [['Nickname edited', formatDateValue(p.nicknameEditedAt)] as [string, React.ReactNode]] : []),
                  // `false` is a real answer ("confirmed public"), so this
                  // checks `!== undefined`, not truthiness, same as above.
                  ...(p.isPrivate !== undefined ? [['Is private', p.isPrivate ? 'Yes' : 'No'] as [string, React.ReactNode]] : []),
                  ...(p.bioLink
                    ? [['Bio link', (
                        // Button only, same style as "Open profile" below.
                        <Box
                          component="a"
                          href={p.bioLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none',
                            px: '10px', py: '5px', borderRadius: '7px', border: `1px solid ${tk.border}`,
                            background: tk.surface3, color: tk.text2, textDecoration: 'none', fontSize: 12, fontWeight: 600,
                            '&:hover': { borderColor: '#2D89F4', color: '#2D89F4' },
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                          Open bio link
                        </Box>
                      )] as [string, React.ReactNode]]
                    : []),
                  // Kept last — a button, not a data field, so it reads
                  // better anchoring the bottom of the list than sitting
                  // between plain text rows.
                  ...(p.profileUrl
                    ? [['Profile URL', (
                        // Button only — the raw URL text isn't shown, just the
                        // clickable control to open it.
                        <Box
                          component="a"
                          href={p.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none',
                            px: '10px', py: '5px', borderRadius: '7px', border: `1px solid ${tk.border}`,
                            background: tk.surface3, color: tk.text2, textDecoration: 'none', fontSize: 12, fontWeight: 600,
                            '&:hover': { borderColor: '#2D89F4', color: '#2D89F4' },
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                          Open profile
                        </Box>
                      )] as [string, React.ReactNode]]
                    : []),
                ]}
              />
              </Box>
            </Box>
          </ResCard>
        </Stack>

        <Stack>
          <ResCard title="Activity and gaps">
            <Duo>
              <Box>
                <FieldTable
                  rows={[
                    ['Posts', String(p.posts ?? '—')],
                    ['Followers', String(p.followers ?? '—')],
                    ['Following', String(p.following ?? '—')],
                    ...(p.likes ? [['Likes', String(p.likes)] as [string, React.ReactNode]] : []),
                    ...(p.friends ? [['Friends', String(p.friends)] as [string, React.ReactNode]] : []),
                    ...(highlightsCount ? [['Highlights', String(highlightsCount)] as [string, React.ReactNode]] : []),
                    ...(storiesCount ? [['Stories', String(storiesCount)] as [string, React.ReactNode]] : []),
                  ]}
                />
              </Box>
              <Box>
                {p.bio ? (
                  <>
                    <Sech>Bio</Sech>
                    <Typography sx={{ fontSize: 12.5, color: tk.text2, lineHeight: 1.5, mt: '4px' }}>{p.bio}</Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12, color: tk.text3 }}>No bio returned by this source.</Typography>
                )}
              </Box>
            </Duo>
          </ResCard>
        </Stack>
      </Cols>

      {/* Each panel only renders when it has content — an empty
       * "No highlights or active stories" or post-count-only Posts panel
       * just wastes space. When only one has content it takes the full
       * width; when neither does, the row is omitted entirely. */}
      {(() => {
        const hasHighlights = highlightsCount > 0 || storiesCount > 0
        const hasPosts = realPosts.length > 0 || postItems.length > 0
        const highlightsCard = (
          <ResCard title="Highlights and Stories" count={highlightsCount + storiesCount || undefined} fill>
            <Stack>
              <HighlightsRow items={highlightItems} />
              <StoriesRow items={storyItems} />
            </Stack>
          </ResCard>
        )
        const postsCard = (
          <ResCard title="Posts" count={realPosts.length > 0 ? realPosts.length : postItems.length} fill>
            {realPosts.length > 0 ? (
              <PostList posts={realPosts} maxHeight={520} />
            ) : (
              <MediaPanel items={postItems} />
            )}
          </ResCard>
        )
        if (hasHighlights && hasPosts) {
          return (
            <Cols n={2} align="stretch">
              <Stack>{highlightsCard}</Stack>
              <Stack>{postsCard}</Stack>
            </Cols>
          )
        }
        if (hasHighlights) return highlightsCard
        if (hasPosts) return postsCard
        return null
      })()}

      <RawCard data={apiData} />
      {lightbox}
    </>
  )
}
